import { useState } from 'react'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { AlertCircle, Layers, Plus, RefreshCw } from 'lucide-react'
import { FuenteFormDialog, FuentesTable } from '../components'
import { useCreateFuente, useFuentes, useUpdateFuente } from '@/modules/bd-unificada/hooks/useFuentes'
import type { Fuente, FuenteCreateInput, FuenteUpdateInput } from '@/modules/bd-unificada/types/fuente'

export function FuentesPage() {
  useDocumentTitle('BD Unificada — Fuentes')

  const [dialogOpen, setDialogOpen] = useState(false)
  const [mode, setMode] = useState<'create' | 'edit'>('create')
  const [fuenteSel, setFuenteSel] = useState<Fuente | null>(null)

  const { data: fuentes, isLoading, refetch } = useFuentes()
  const createFuente = useCreateFuente()
  const updateFuente = useUpdateFuente()

  const handleCreate = () => {
    setMode('create')
    setFuenteSel(null)
    setDialogOpen(true)
  }

  const handleEdit = (f: Fuente) => {
    setMode('edit')
    setFuenteSel(f)
    setDialogOpen(true)
  }

  const handleSubmit = async (data: FuenteCreateInput | FuenteUpdateInput) => {
    if (mode === 'create') {
      await createFuente.mutateAsync(data as FuenteCreateInput)
    } else if (fuenteSel) {
      await updateFuente.mutateAsync({ id: fuenteSel.id, data: data as FuenteUpdateInput })
    }
    setDialogOpen(false)
    setFuenteSel(null)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Layers className="h-6 w-6 text-blue-500" />
          BD Unificada — Fuentes
        </h1>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={handleCreate} className="bg-blue-800 hover:bg-blue-900 text-white">
            <Plus className="mr-2 h-4 w-4" /> Nueva fuente
          </Button>
        </div>
      </div>

      <Card className="border border-gray-300 dark:border-gray-700 p-6">
        {isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : fuentes && fuentes.length > 0 ? (
          <FuentesTable fuentes={fuentes} onEdit={handleEdit} />
        ) : (
          <div className="p-12 text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <h3 className="mt-4 text-lg font-semibold">No hay fuentes registradas</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Crea una fuente para poder asociar cargas de contactos.
            </p>
          </div>
        )}
      </Card>

      <FuenteFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSubmit={handleSubmit}
        isSubmitting={createFuente.isPending || updateFuente.isPending}
        fuente={fuenteSel}
        mode={mode}
      />
    </div>
  )
}
